import { Add } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  ButtonProps,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import React from 'react'
import { connect } from 'react-redux'
import NivosModal from 'components/nivos-modal'
import NivosRTE from 'components/nivos-rte'
import {
  Operations as IncidentReportOperations,
  operations as incidentReportOperations
} from 'states/dux/incident-report/operations'
import { Payload as IncidentReportPayload } from 'states/dux/incident-report/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { IncidentReport } from 'types/incident-report'
import { NivosUser } from 'types/user'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields,
  post
} from 'utils/common'

// TODO: API-Error handling on creation form

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  incidentReport: IncidentReportPayload
  user: UserPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    incidentReport: IncidentReportOperations
    user: UserOperations
  }
}

interface OwnProps {
  noButton?: boolean
  title?: string
  buttonProps?: ButtonProps
  selectedReport?: IncidentReport
  setSelectedReport?: (report: IncidentReport | undefined) => void
  useIconButton?: boolean
}

interface SelectFieldOptions {
  label: string
  value: number
}

type AppProps = StateProps & DispatchProps & OwnProps

const CreateIncidentReportFC: React.FC<any> = (props: AppProps) => {
  const {
    buttonProps,
    noButton,
    selectedReport,
    setSelectedReport,
    user,
    operations,
    useIconButton
  } = props
  const [open, setOpen] = React.useState(false)
  const [location, setLocation] = React.useState('Building')
  const [floor, setFloor] = React.useState(1)
  const [amenity, setAmenity] = React.useState('Infinity Pool')
  const [type, setType] = React.useState('RP')
  const [images, setImages] = React.useState<File>()
  const [titleInput, setTitleInput] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [unit, setUnit] = React.useState('')
  const [formLoading, setFormLoading] = React.useState(false)
  const [rte, setRte] = React.useState('')
  const [status, setStatus] = React.useState('Pending')
  const isMobileView = useMediaQuery('(max-width:600px)')
  const [inputValue, setInputValue] = React.useState('')

  const [userSelection, setUserSelection] =
    React.useState<SelectFieldOptions | null>(null)

  const [userSelectOption, setUserOption] = React.useState<
  SelectFieldOptions[]
  >([])

  const defaultErrorsState = {
    location: false,
    type: false,
    title: false,
    images: false,
    description: false,
    unit: false,
    managedBy: false,
    status: false
  }

  const [errors, setErrors] = React.useState(defaultErrorsState)

  const statusSeletion = [
    'Pending',
    'Accepted',
    'Declined',
    'Assigned',
    'InProgress',
    'Resolved'
  ]

  const locationSelection = ['Building', 'Unit', 'Amenity']
  const floorSelection = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
  const typeSelection = [
    {
      label: 'Repair',
      code: 'RP'
    },
    {
      label: 'Cleaning',
      code: 'CL'
    },
    {
      label: 'Maintenance',
      code: 'MN'
    }
  ]

  const amenitySelection = [
    { label: 'Infinity Pool', value: 'Infinity Pool' },
    { label: 'Gym', value: 'Gym' },
    { label: 'Basketball court', value: 'Basketball court' },
    { label: 'Karaoke Room', value: 'Karaoke Room' },
    { label: 'Game Room', value: 'Game Room' }
  ]

  const crunchLocationStates = (): string => {
    let locationStr = ''

    if (location === 'Building') {
      locationStr = `Floor number ${floor} of the Building`
    }

    if (location === 'Amenity') {
      locationStr = amenity
    }

    if (location === ' Unit') {
      locationStr = `Unit ${unit} Floor ${floor}`
    }

    return locationStr
  }

  const generateFormData = (): FormData => {
    const formData = new FormData()
    if (images instanceof File) {
      formData.append('images', images, images.name)
    }
    formData.append('title', titleInput)
    formData.append('description', description)
    formData.append('description_rte', rte)
    formData.append('required_action', type)
    formData.append('location', crunchLocationStates())
    formData.append('status', status.substring(0, 3).toUpperCase())

    if (userSelection !== null) {
      formData.append('managed_by', userSelection.value.toString())
    }

    // TODO: Created by and Updated by fields should be current user
    formData.append('updated_by', user.id.toString())
    formData.append('created_by', user.id.toString())

    return formData
  }

  const clearForm = (): void => {
    setLocation('Building')
    setFloor(1)
    setAmenity('Infinity Pool')
    setType('RP')
    setImages(undefined)
    setTitleInput('')
    setDescription('')
    setUnit('')
    setErrors(defaultErrorsState)
  }

  const validateInputs = (): void => {
    let errorState = { ...errors }
    let hasError = false
    const requiredInputs = [
      { label: 'location', val: location },
      { label: 'type', val: type },
      { label: 'title', val: titleInput },
      { label: 'description', val: description },
      { label: 'images', val: images },
      { label: 'unit', val: unit, conditional: true }
    ]

    setFormLoading(true)

    // Validate required fields
    requiredInputs.forEach(input => {
      if (
        (input.val == null || input.val === '') &&
        input.conditional !== true
      ) {
        errorState = { ...errorState, [input.label]: true }
        // TODO: Add image support for admin
        if (input.label !== 'images') hasError = true
      } else {
        errorState = { ...errorState, [input.label]: false }
      }
    })

    // Validate required conditional fields
    if (location === 'Unit') {
      if (unit == null || unit === '') {
        errorState = { ...errorState, unit: true }
      } else {
        errorState = { ...errorState, unit: false }
      }
    }

    setErrors({ ...errorState })

    if (hasError) {
      setFormLoading(false)
      return
    }

    operations.incidentReport.loading(true)
    if (selectedReport !== undefined && selectedReport.id > 0) {
      void post(
        `/report/${selectedReport.id}/`,
        generateFormData(),
        true,
        false,
        'PUT'
      ).then(data => {
        operations.incidentReport.update(objectifyDateStringFields(data))
        clearForm()
        setFormLoading(false)
        operations.incidentReport.loading(false)
        setOpen(false)
      })
    } else {
      void post('/report/', generateFormData(), true).then(data => {
        operations.incidentReport.add(objectifyDateStringFields(data))
        clearForm()
        setFormLoading(false)
        operations.incidentReport.loading(false)
        setOpen(false)
      })
    }
  }

  React.useEffect(() => {
    if (selectedReport !== undefined && selectedReport.id > 0) {
      setType(
        typeSelection.find(item => item.label === selectedReport.requiredAction)
          ?.code ?? 'RP'
      )
      setTitleInput(selectedReport.title)
      setRte(selectedReport.descriptionRte)
      setDescription(selectedReport.description)
      setStatus(selectedReport.status.replace('-', ''))
      if (selectedReport.location !== undefined) {
        setLocation(selectedReport.location)
      }
      if (
        selectedReport.managedBy !== undefined &&
        selectedReport.managedBy !== null
      ) {
        setUserSelection({
          label: selectedReport.managedBy.fullName,
          value: selectedReport.managedBy.id
        })
      }
    }
  }, [selectedReport])

  React.useEffect(() => {
    void get('/account/').then((data: NivosUser[]) => {
      try {
        setUserOption(
          data.map(item => ({ label: item.fullName, value: item.id }))
        )
      } catch (e) {
        console.log(e)
      }
    })
  }, [])

  return (
    <Box>
      <NivosModal
        title={
          (selectedReport !== undefined && selectedReport.id > 0
            ? 'UPDATE'
            : 'NEW') + ' REPORT'
        }
        open={noButton === true ? selectedReport !== undefined : open}
        setOpen={
          noButton === true && setSelectedReport !== undefined
            ? _ => setSelectedReport(undefined)
            : setOpen
        }
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          minWidth={isMobileView ? '92vw' : 700}
          width={isMobileView ? '92vw' : 700}
          paddingX={2}
          paddingTop={4}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} sm={3}>
              <Typography variant={'h6'} flexGrow={1}>
                Title:{' '}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField
                error={errors.title}
                type={'text'}
                fullWidth
                value={titleInput}
                onChange={event => setTitleInput(event.currentTarget.value)}
              />
            </Grid>
            {selectedReport === undefined && (
              <Grid item xs={12} sm={3}>
                <Typography variant={'h6'}>Location: </Typography>
              </Grid>
            )}
            {selectedReport === undefined && (
              <Grid item xs={12} sm={9}>
                <TextField
                  error={errors.location}
                  type={'text'}
                  select
                  fullWidth
                  value={location}
                  onChange={event => {
                    setLocation(event.target.value)
                  }}
                >
                  {locationSelection.map(location => (
                    <MenuItem value={location} key={location}>
                      {location}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {selectedReport === undefined && (
              <Grid item xs={12} sm={3}>
                <Typography variant={'h6'} flexGrow={1}>
                  Floor:{' '}
                </Typography>
              </Grid>
            )}
            {selectedReport === undefined && (
              <Grid item xs={12} sm={9}>
                <TextField
                  type={'text'}
                  select
                  fullWidth
                  value={floor}
                  onChange={event => setFloor(parseInt(event.target.value))}
                >
                  {floorSelection.map(floor => (
                    <MenuItem value={floor} key={floor}>
                      {floor}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {selectedReport === undefined && location === 'Unit' && (
              <Grid item xs={12} sm={3}>
                <Typography variant={'h6'} flexGrow={1}>
                  Unit:{' '}
                </Typography>
              </Grid>
            )}
            {selectedReport === undefined && location === 'Unit' && (
              <Grid item xs={12} sm={9}>
                <TextField
                  fullWidth
                  error={errors.unit}
                  value={unit}
                  onChange={event => setUnit(event.currentTarget.value)}
                />
              </Grid>
            )}
            {location === 'Amenity' && (
              <Grid item xs={12} sm={3}>
                <Typography variant={'h6'} flexGrow={1}>
                  Choose location:{' '}
                </Typography>
              </Grid>
            )}
            {location === 'Amenity' && (
              <Grid item xs={12} sm={9}>
                <TextField
                  type={'text'}
                  select
                  fullWidth
                  value={amenity}
                  onChange={event => setAmenity(event.target.value)}
                >
                  {amenitySelection.map(amenity => (
                    <MenuItem value={amenity.value} key={amenity.value}>
                      {amenity.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} sm={3}>
              <Typography variant={'h6'} flexGrow={1}>
                Issue type:{' '}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField
                select
                error={errors.type}
                type={'text'}
                fullWidth
                value={type}
                onChange={event => setType(event.target.value)}
              >
                {typeSelection.map(type => (
                  <MenuItem value={type.code} key={type.code}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {selectedReport !== undefined &&
              selectedReport.id > 0 &&
              selectedReport.status !== '' && (
                <Grid item xs={12} sm={3}>
                  <Typography variant={'h6'} flexGrow={1}>
                    Assigned Person:{' '}
                  </Typography>
                </Grid>
            )}
            {selectedReport !== undefined &&
              selectedReport.id > 0 &&
              selectedReport.status !== '' && (
                <Grid item xs={12} sm={9}>
                  <Autocomplete
                    disablePortal
                    multiple={false}
                    options={userSelectOption}
                    value={userSelection}
                    inputValue={inputValue}
                    onInputChange={(_, value) => setInputValue(value)}
                    onChange={(event, value) => {
                      if (value !== null) {
                        setUserSelection(value)
                      } else {
                        setUserSelection(null)
                      }
                      setErrors({ ...errors, managedBy: false })
                    }}
                    isOptionEqualToValue={() => true}
                    renderInput={params => (
                      <TextField
                        {...params}
                        fullWidth
                        error={errors.managedBy}
                        label="Assigned Person"
                      />
                    )}
                  />
                </Grid>
            )}
            {selectedReport !== undefined &&
              selectedReport.id > 0 &&
              selectedReport.status !== '' && (
                <Grid item xs={12} sm={3}>
                  <Typography variant={'h6'} flexGrow={1}>
                    Status:{' '}
                  </Typography>
                </Grid>
            )}
            {selectedReport !== undefined &&
              selectedReport.id > 0 &&
              selectedReport.status !== '' && (
                <Grid item xs={12} sm={9}>
                  <TextField
                    error={errors.status}
                    type={'text'}
                    select
                    fullWidth
                    value={status}
                    onChange={event => {
                      setStatus(event.target.value)
                    }}
                  >
                    {statusSeletion.map(_statusName => (
                      <MenuItem value={_statusName} key={_statusName}>
                        {_statusName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
            )}
            <Grid item xs={12} sm={3}>
              <Typography variant={'h6'} flexGrow={1}>
                Description:{' '}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <Box
                width={1}
                border={errors.description ? '1px solid red' : 'none'}
              >
                <NivosRTE
                  rteState={rte}
                  setRteState={setRte}
                  setPLainState={setDescription}
                />
              </Box>
            </Grid>
            {(selectedReport === undefined || selectedReport.id === 0) && (
              <Grid item xs={12} sm={3}>
                <Typography variant={'h6'} flexGrow={1}>
                  Images:{' '}
                </Typography>
              </Grid>
            )}
            {(selectedReport === undefined || selectedReport.id === 0) && (
              <Grid item xs={12} sm={9}>
                <input
                  style={{
                    border: errors.images ? '1px solid red' : 'none',
                    width: '100%',
                    borderRadius: '4px'
                  }}
                  type={'file'}
                  accept={'image/*'}
                  onChange={event => {
                    if (event.currentTarget.files != null) {
                      setImages(event.currentTarget.files[0])
                    }
                  }}
                />
              </Grid>
            )}
          </Grid>
          <Box
            display={'flex'}
            justifyContent={'flex-end'}
            padding={1}
            paddingTop={isMobileView ? 4 : 1}
          >
            <Button
              variant={'contained'}
              color={'primary'}
              size={'large'}
              onClick={() => validateInputs()}
              disabled={formLoading}
            >
              {formLoading ? (
                <CircularProgress />
              ) : selectedReport === undefined ? (
                'Submit Report'
              ) : (
                'Update Report'
              )}
            </Button>
          </Box>
        </Box>
      </NivosModal>
      {!(noButton === true) && (
        <>
          {useIconButton !== undefined && useIconButton && (
            <IconButton onClick={() => setOpen(true)}>
              <Add />
            </IconButton>
          )}
          {(useIconButton === undefined || !useIconButton) && (
            <Button
              variant={'outlined'}
              size={'large'}
              startIcon={<Add />}
              sx={{ borderRadius: 4, borderWidth: 2 }}
              onClick={() => setOpen(true)}
              {...buttonProps}
            >
              SUBMIT
            </Button>
          )}
        </>
      )}
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    incidentReport: state.incidentReport,
    user: state.user
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  incidentReport: incidentReportOperations,
  user: userOperations
})

const CreateIncidentReport = connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateIncidentReportFC) as React.FC<OwnProps>

export default CreateIncidentReport
